/**
 * <spivex-set-finder>: the quiz of sections/spivex-set-finder.liquid.
 *
 * Shows one panel at a time: intro → questions (blocks/_spivex-quiz-question.liquid) → short pause → result.
 * A tap on an answer marks it and moves on. The result follows from the effects of the chosen answers:
 * - "komplett" anywhere → Komplett size, otherwise the section's default size,
 * - "plus" anywhere → the Plus family (metal and plastic rotors),
 * - "dyson" / "vorwerk" → "Für deinen Staubsauger:" with the adapter as a tick box (never preselected),
 * - "note" → the answer's line as a hint; every other answer's line goes into "Darum dieses Set".
 * - "kontakt" → the box "Kontakt-Hinweis" (mail and phone links, section setting) in the shown package (29.10.2026).
 * The result packages are server-rendered with data-result="basis|komplett|basis_plus|komplett_plus"; the add-on box
 * (adapter, greeting card) moves into the shown package. Prices and the cart: snippets/spivex-zusatz.
 * "Paket ansehen" stores the vacuum answer's adapter and the shopper's own ticks for the package page
 * (sessionStorage, read once by assets/spivex-zusatz.js).
 *
 * ?set-finder=N answers question 1 with answer N and opens question 2.
 * In the theme editor, selecting a question block opens that question.
 */
const TAKE_OVER = 'spivex-paket-zusaetze';

/* Styles für „hinzufügen“ und die Rückfrage – direkt hier, damit sie sofort wirken (26.10.2026). */
(function addStyles() {
  if (document.getElementById('spivex-set-finder-stil')) return;
  const style = document.createElement('style');
  style.id = 'spivex-set-finder-stil';
  style.textContent = `
    .spivex-quiz__tick:has(input:checked) .spivex-quiz__tick-verb { display: none; }
    .spivex-quiz .spivex-quiz__after { align-items: center; }
    .spivex-quiz .spivex-quiz__after > * { display: inline-flex; align-items: center; gap: 6px; margin: 0; padding-block: 0; line-height: 1.4; }
    .spivex-quiz-dialog { max-width: min(26rem, calc(100vw - 32px)); padding: 0; border: 0; border-radius: 24px;
      background: var(--spivex-card, #fff); color: var(--spivex-ink, #1d1d1f); box-shadow: 0 18px 50px rgb(0 0 0 / 0.22); }
    .spivex-quiz-dialog::backdrop { background: rgb(0 0 0 / 0.4); }
    .spivex-quiz-dialog__inner { display: grid; gap: 14px; padding: 24px; text-align: center; }
    .spivex-quiz-dialog__title { margin: 0; font-size: 1.15rem; font-weight: 700; }
    .spivex-quiz-dialog__text { margin: 0; line-height: 1.5; }
    .spivex-quiz-dialog__actions { display: grid; gap: 8px; margin-top: 4px; }
    .spivex-quiz-dialog__actions .button { width: 100%; white-space: normal; }
  `;
  document.head.appendChild(style);
})();

/**
 * „Satz im Ergebnis“ kann fett, kursiv, Links und Absätze enthalten (richtext, 26.10.2026).
 * Übernimmt nur diese Auszeichnungen; Absätze werden zu Zeilenumbrüchen, alles andere wird reiner Text.
 * @param {HTMLElement} target
 * @param {string} html
 */
function setRichText(target, html) {
  const ALLOWED = new Set(['A', 'STRONG', 'B', 'EM', 'I', 'BR']);
  const source = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body;
  /** @param {Node} node @returns {Node[]} */
  const copy = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return [document.createTextNode(node.textContent || '')];
    if (!(node instanceof Element)) return [];
    const children = [...node.childNodes].flatMap(copy);
    if (!ALLOWED.has(node.tagName)) return children;
    const element = document.createElement(node.tagName.toLowerCase());
    if (node.tagName === 'A') {
      const href = node.getAttribute('href') || '';
      if (/^(https?:|mailto:|tel:|\/|#)/i.test(href)) element.setAttribute('href', href);
      if (node.getAttribute('target') === '_blank') {
        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noopener');
      }
    }
    element.append(...children);
    return [element];
  };
  // Absätze (<p>) als Zeilen mit Umbruch dazwischen, damit sie in die Liste bzw. den Hinweis passen.
  const nodes = [];
  for (const child of source.childNodes) {
    if (child instanceof Element && child.tagName === 'P') {
      if (nodes.length) nodes.push(document.createElement('br'));
      nodes.push(...[...child.childNodes].flatMap(copy));
    } else {
      nodes.push(...copy(child));
    }
  }
  target.replaceChildren(...nodes);
}

/** 29.10.2026: Antworten vom letzten Mal (localStorage, nur auf diesem Gerät) */
const LETZTE = 'spivex-set-finder-antworten';

class SpivexSetFinder extends HTMLElement {
  /** @type {HTMLElement[]} */
  #questions = [];
  /** @type {(HTMLButtonElement | null)[]} */
  #chosen = [];
  /** @type {number} -1 = intro, 0…n-1 = question, n = result */
  #index = -1;
  /** @type {number | undefined} */
  #timer;
  /** @type {string[]} Wirkungen der gewählten Antworten (für die Rückfrage vor dem Warenkorb) */
  #effects = [];
  /** Nach „Ohne … in den Warenkorb“: der nächste Klick geht ohne Rückfrage durch */
  #confirmed = false;

  connectedCallback() {
    this.#questions = [...this.querySelectorAll('[data-panel="question"]')];
    if (!this.#questions.length) return;

    this.querySelector('[data-action="start"]')?.removeAttribute('hidden');
    this.querySelector('[data-fallback]')?.setAttribute('hidden', '');
    this.addEventListener('click', this.#onClick);
    // Vor allen anderen Klick-Verarbeitungen (Capture), damit die Rückfrage den Warenkorb-Klick anhalten kann.
    this.addEventListener('click', this.#onBuyClick, true);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);

    this.#letztesZeigen();

    const start = Number(new URL(window.location.href).searchParams.get('set-finder'));
    const answer = start > 0 ? this.#questions[0].querySelectorAll('[data-answer]')[start - 1] : null;
    if (answer instanceof HTMLButtonElement) {
      this.#choose(answer);
      this.#show(1, false);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('click', this.#onBuyClick, true);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    window.clearTimeout(this.#timer);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const answer = event.target.closest('[data-answer]');
    if (answer instanceof HTMLButtonElement) {
      this.#choose(answer);
      const next = this.#index + 1;
      // A short beat so the tap is visible before the next question slides in.
      window.clearTimeout(this.#timer);
      this.#timer = window.setTimeout(() => this.#show(next), this.#reducedMotion ? 0 : 220);
      return;
    }

    const link = event.target.closest('[data-paket-link]');
    if (link instanceof HTMLAnchorElement) {
      this.#rememberAddOns(link);
      return;
    }

    const action = event.target.closest('[data-action]')?.getAttribute('data-action');
    if (action === 'start') this.#show(0);
    if (action === 'letztes') this.#letztesNehmen();
    if (action === 'back') this.#show(Math.min(this.#index, this.#questions.length) - 1);
    if (action === 'restart') {
      this.#chosen = [];
      for (const button of this.querySelectorAll('[data-answer]')) button.setAttribute('aria-pressed', 'false');
      this.#show(0);
    }
  };

  /**
   * 29.10.2026: Antworten vom letzten Mal (nur auf diesem Gerät, im Browser gespeichert, sobald ein Ergebnis kam).
   * @returns {HTMLButtonElement[] | null} die gemerkten Antworten, wenn sie zu den heutigen Fragen passen
   */
  #letzteAntworten() {
    let gemerkt;
    try {
      gemerkt = JSON.parse(window.localStorage.getItem(LETZTE) || 'null');
    } catch {
      return null;
    }
    const antworten = Array.isArray(gemerkt?.antworten) ? gemerkt.antworten : null;
    if (!antworten || antworten.length !== this.#questions.length) return null;
    const buttons = this.#questions.map(
      (question, i) => question.querySelectorAll('[data-answer]')[Number(antworten[i]) - 1] || null
    );
    return buttons.every((button) => button instanceof HTMLButtonElement)
      ? /** @type {HTMLButtonElement[]} */ (buttons)
      : null;
  }

  #letztesZeigen() {
    const box = this.querySelector('[data-letztes]');
    if (!(box instanceof HTMLElement)) return;
    const buttons = this.#letzteAntworten();
    box.toggleAttribute('hidden', !buttons);
    const liste = box.querySelector('[data-letztes-liste]');
    if (buttons && liste) {
      liste.textContent = buttons
        .map((button) => button.querySelector('.spivex-quiz__answer-label')?.textContent?.trim() || '')
        .filter(Boolean)
        .join(' · ');
    }
  }

  #letztesNehmen() {
    const buttons = this.#letzteAntworten();
    if (!buttons) return;
    for (const button of buttons) this.#choose(button);
    this.#show(this.#questions.length);
  }

  #letztesMerken() {
    const antworten = this.#questions.map((question, i) => {
      const button = this.#chosen[i];
      return button ? [...question.querySelectorAll('[data-answer]')].indexOf(button) + 1 : 0;
    });
    if (antworten.some((nummer) => nummer < 1)) return;
    try {
      window.localStorage.setItem(LETZTE, JSON.stringify({ antworten }));
    } catch {
      // ohne Speicher (z. B. privater Modus) gibt es den Knopf eben nicht
    }
  }

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const blockId = /** @type {CustomEvent} */ (event).detail?.blockId;
    const index = this.#questions.findIndex((question) => question.dataset.blockId === blockId);
    if (index >= 0) this.#show(index, false);
  };

  /** @param {HTMLButtonElement} answer */
  #choose(answer) {
    const question = answer.closest('[data-panel="question"]');
    const index = this.#questions.indexOf(/** @type {HTMLElement} */ (question));
    if (index < 0) return;
    for (const button of this.#questions[index].querySelectorAll('[data-answer]')) {
      button.setAttribute('aria-pressed', String(button === answer));
    }
    this.#chosen[index] = answer;
  }

  /**
   * @param {number} index - -1 intro, 0…n-1 question, n result
   * @param {boolean} [focus] - Move focus to the new panel's heading (not on page load)
   */
  #show(index, focus = true) {
    window.clearTimeout(this.#timer);
    const total = this.#questions.length;
    this.#index = Math.max(-1, Math.min(index, total));

    if (this.#index === total) {
      const pause = this.#reducedMotion ? 0 : Number(this.dataset.loadingMs || 0);
      if (pause > 0) {
        this.#activate(this.querySelector('[data-panel="loading"]'), focus);
        this.style.setProperty('--spivex-quiz-loading', `${pause}ms`);
        this.#timer = window.setTimeout(() => this.#activate(this.#renderResult(), focus), pause);
      } else {
        this.#activate(this.#renderResult(), focus);
      }
    } else if (this.#index >= 0) {
      this.#activate(this.#questions[this.#index], focus);
    } else {
      this.#activate(this.querySelector('[data-panel="intro"]'), focus);
    }

    this.#updateProgress();
  }

  /**
   * @param {Element | null} panel
   * @param {boolean} focus
   */
  #activate(panel, focus) {
    if (!(panel instanceof HTMLElement)) return;
    for (const other of this.querySelectorAll('[data-panel]')) other.toggleAttribute('hidden', other !== panel);

    if (!focus) return;
    const heading = [...panel.querySelectorAll('[data-focus]')].find(
      (element) => element instanceof HTMLElement && !element.closest('[hidden]')
    );
    if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
    if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  #updateProgress() {
    const total = this.#questions.length;
    const bar = this.querySelector('[data-bar]');
    const inQuiz = this.#index >= 0 && this.#index < total;
    bar?.toggleAttribute('hidden', !inQuiz);
    if (!inQuiz) return;

    const fill = this.querySelector('[data-progress-fill]');
    // Starts with a visible piece so the first question already feels under way.
    if (fill instanceof HTMLElement) fill.style.width = `${Math.round(((this.#index + 1) / (total + 1)) * 100)}%`;
    const text = this.querySelector('[data-progress-text]');
    if (text) {
      text.textContent = (this.dataset.progressLabel || '')
        .replace('[current]', String(this.#index + 1))
        .replace('[total]', String(total));
    }
  }

  /** @returns {HTMLElement | null} the result panel, filled for the current answers */
  #renderResult() {
    const effects = this.#chosen.filter(Boolean).map((answer) => answer?.dataset.effect || 'none');
    this.#effects = effects;
    const size = effects.includes('komplett') ? 'komplett' : this.dataset.defaultSize || 'basis';
    const family = effects.includes('plus') ? '_plus' : '';

    const cards = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-result]')]);
    const pick =
      cards.find((card) => card.dataset.result === `${size}${family}`) ||
      cards.find((card) => card.dataset.result?.startsWith(size)) ||
      cards[0];
    for (const card of cards) card.toggleAttribute('hidden', card !== pick);

    const reasons = [];
    const notes = [];
    for (const answer of this.#chosen) {
      const line = answer?.dataset.reason?.trim();
      if (!line) continue;
      if (answer?.dataset.effect === 'kontakt') continue;
      if (answer?.dataset.effect === 'note') notes.push(line);
      else if (!reasons.includes(line)) reasons.push(line);
    }

    const slot = pick?.querySelector('[data-reasons-slot]');
    if (slot) {
      slot.replaceChildren();
      const wrapper = this.#template('[data-reasons-template]');
      const list = wrapper?.querySelector('ul');
      if (wrapper && list && reasons.length) {
        for (const reason of reasons.slice(0, 5)) {
          const item = this.#template('[data-reason-template]');
          const text = item?.querySelector('span');
          if (!item || !text) continue;
          setRichText(text, reason);
          list.append(item);
        }
        slot.append(wrapper);
      }
    }

    const note = this.querySelector('[data-note]');
    const noteText = note?.querySelector('span');
    if (note && noteText) {
      setRichText(noteText, notes.join(' '));
      note.toggleAttribute('hidden', notes.length === 0);
    }

    // 29.10.2026: „Weiß nicht“ (Wirkung „kontakt“): Kontakt-Box mit Mail und Telefon wie im Staubsauger-Check,
    // im gezeigten Paket über den Zusätzen
    // 29.10.2026 (2): fürs Adapter-Fenster (snippets/spivex-adapter-fenster): nur wenn bei der Staubsauger-Frage
    // Dyson, Vorwerk oder „Weiß nicht“ gewählt wurde, bei „Rundes Rohr“ nicht
    this.dataset.adapterFrage = ['dyson', 'vorwerk', 'kontakt'].some((e) => effects.includes(e)) ? 'ja' : 'nein';

    const kontakt = this.querySelector('[data-kontakt]');
    if (kontakt) {
      const slot = pick?.querySelector('[data-addons-slot]');
      if (slot) slot.before(kontakt);
      kontakt.toggleAttribute('hidden', !effects.includes('kontakt'));
    }

    const addOns = this.querySelector('[data-addons]');
    if (addOns) pick?.querySelector('[data-addons-slot]')?.append(addOns);
    for (const addon of this.querySelectorAll('[data-addon]')) {
      const shown = effects.includes(addon.getAttribute('data-addon') || '');
      addon.toggleAttribute('hidden', !shown);
      // A tick from an earlier round must not add an adapter that no longer fits the answers.
      const box = addon.querySelector('input[data-spivex-zusatz]');
      if (!shown && box instanceof HTMLInputElement && box.checked) {
        box.checked = false;
        box.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.#letztesMerken();

    // Custom event for Shopify's Customer Events (pixels); consent is handled there.
    window.Shopify?.analytics?.publish?.('spivex_set_finder_result', {
      result: pick?.dataset.result,
      answers: this.#chosen.map((answer) => answer?.dataset.answer || ''),
    });
    // Same result for Google Ads (assets/spivex-google-tags.js, only with marketing consent).
    this.dispatchEvent(
      new CustomEvent('spivex:set-finder-ergebnis', { bubbles: true, detail: { result: pick?.dataset.result } }),
    );

    return this.querySelector('[data-panel="result"]');
  }

  /**
   * „In den Warenkorb“ im Ergebnis: Ist der passende Adapter (Dyson/Vorwerk-Antwort) nicht angehakt, kurz nachfragen. Nichts wird vorausgewählt (§ 312a Abs. 3 BGB);
   * der Kunde entscheidet aktiv: „Zurück“ oder „Ohne … in den Warenkorb“.
   * @param {MouseEvent} event
   */
  #onBuyClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-result] form button[type="submit"]');
    if (!(button instanceof HTMLButtonElement)) return;
    if (this.#confirmed) {
      this.#confirmed = false;
      return;
    }

    const missing = [];
    // Nur die Adapter, nicht die Grußkarte (data-addon="geschenk", 28.10.2026)
    const adapter = [...this.querySelectorAll('[data-addon]:not([data-addon="geschenk"])')].find((addon) => !addon.hasAttribute('hidden'));
    const adapterBox = adapter?.querySelector('input[data-spivex-zusatz]');
    if (adapterBox instanceof HTMLInputElement && !adapterBox.checked) {
      missing.push({ short: 'Adapter', name: adapter?.getAttribute('data-addon-name') || 'Adapter' });
    }
    if (!missing.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#askBeforeCart(button);
  };

  /**
   * Rückfrage nur zum Adapter (Grußkarte ausgenommen, 26.10.2026).
   * @param {HTMLButtonElement} button
   */
  #askBeforeCart(button) {
    const message = 'Es ist kein Adapter ausgewählt.';
    const without = 'Ohne Adapter in den Warenkorb';

    const dialog = document.createElement('dialog');
    dialog.className = 'spivex-quiz-dialog spivex-ui';
    dialog.setAttribute('aria-labelledby', 'SpivexQuizDialogTitle');
    dialog.innerHTML = `
      <div class="spivex-quiz-dialog__inner">
        <p class="spivex-quiz-dialog__title" id="SpivexQuizDialogTitle"></p>
        <p class="spivex-quiz-dialog__text"></p>
        <div class="spivex-quiz-dialog__actions">
          <button class="button spivex-button" type="button" data-dialog="back">Zurück</button>
          <button class="button button-secondary spivex-button spivex-button--ghost" type="button" data-dialog="go"></button>
        </div>
      </div>`;
    const title = dialog.querySelector('.spivex-quiz-dialog__title');
    const text = dialog.querySelector('.spivex-quiz-dialog__text');
    const go = dialog.querySelector('[data-dialog="go"]');
    if (title) title.textContent = 'Bist du sicher?';
    if (text) text.textContent = message;
    if (go) go.textContent = without;

    const close = () => {
      dialog.close();
      dialog.remove();
    };
    dialog.addEventListener('click', (event) => {
      const action = event.target instanceof Element ? event.target.closest('[data-dialog]')?.getAttribute('data-dialog') : null;
      if (action === 'back' || event.target === dialog) {
        close();
        const box = this.querySelector('[data-addon]:not([hidden]):not([data-addon="geschenk"]) input[data-spivex-zusatz]');
        if (box instanceof HTMLElement) box.closest('.spivex-quiz__addon')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } else if (action === 'go') {
        close();
        this.#confirmed = true;
        button.click();
      }
    });
    dialog.addEventListener('cancel', () => dialog.remove());
    document.body.append(dialog);
    dialog.showModal();
    /** @type {HTMLElement | null} */ (dialog.querySelector('[data-dialog="back"]'))?.focus();
  }

  /**
   * For "Paket ansehen": the adapter of the vacuum answer (the package page answers its vacuum check with it) and
   * what the shopper ticked here. Only a click writes this, so no link can preselect anything on the package page.
   * @param {HTMLAnchorElement} link
   */
  #rememberAddOns(link) {
    const adapter = [...this.querySelectorAll('[data-addon]:not([data-addon="geschenk"])')]
      .find((addon) => !addon.hasAttribute('hidden'))
      ?.querySelector('input[data-spivex-zusatz]');
    const gift = this.querySelector('.spivex-gift__box');
    const giftFields = gift instanceof HTMLInputElement && gift.checked ? this.querySelector('.spivex-gift__form') : null;
    const giftData = giftFields instanceof HTMLFormElement ? new FormData(giftFields) : null;

    const data = {
      path: new URL(link.href).pathname,
      time: Date.now(),
      adapter: adapter instanceof HTMLInputElement ? adapter.dataset.variantId : null,
      adapterTicked: adapter instanceof HTMLInputElement && adapter.checked,
      gift: giftData
        ? Object.fromEntries([...giftData.entries()].filter(([, value]) => typeof value === 'string'))
        : null,
    };
    try {
      if (data.adapter || data.gift) sessionStorage.setItem(TAKE_OVER, JSON.stringify(data));
      else sessionStorage.removeItem(TAKE_OVER);
    } catch {
      // Private mode without storage: the package page simply starts without ticks.
    }
  }

  /**
   * @param {string} selector
   * @returns {HTMLElement | null} a fresh copy of the template's first element
   */
  #template(selector) {
    const template = this.querySelector(selector);
    if (!(template instanceof HTMLTemplateElement)) return null;
    const element = template.content.firstElementChild?.cloneNode(true);
    return element instanceof HTMLElement ? element : null;
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('spivex-set-finder')) customElements.define('spivex-set-finder', SpivexSetFinder);